/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'
import { Card, CardBody, CardFooter, Container, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import {BrowserRouter as Router} from 'react-router-dom'
import AppRoutes from './AppRoutes.js'
import MainMenu from './MainMenu.js'
import PopupNotification from './widgets/PopupNotification.js'
import UserSession from './widgets/UserSession'
import { AppContext } from './AppContext.js'
import DbTwig from './widgets/DbTwig.js';
import AppLocalStorage from './widgets/AppLocalStorage.js'

import './css/AsterionDB.css'

import preval from 'preval.macro'

import { CopyToClipboard } from 'react-copy-to-clipboard';

window.moment = require('moment');
window.timezone = require('moment-timezone');
window.webAppVersion = 'tag: ' + require('../../package.json').gitTag + ' branch: ' + require('../../package.json').gitBranch;
window.buildDate = preval`module.exports = new Date().toLocaleString();`

class App extends React.Component
{  
  componentMounted = false;
  dbTwig = null;

  async authorize(userSessionData)
  {
    var websiteProfile;
    var userUiProfile;
    let appContext = this.state.appContext;

    appContext.userSession.saveUserSessionData(userSessionData)
    
    switch (userSessionData.sessionStatus)
    {
      case 'active':
      case 'change password':
        userUiProfile = await this.fetchUserUiProfile();
        websiteProfile = await this.fetchWebsiteProfile(false);
        appContext.appLocalStorage = new AppLocalStorage(userUiProfile.apiUserId);
        appContext.websiteProfile = websiteProfile;
        appContext.userUiProfile = userUiProfile;
        this.postNotification('success', 'Login successful');
        break;

      case 'session limit':
        userUiProfile = await this.fetchUserUiProfile();
        websiteProfile = await this.fetchWebsiteProfile(false);
        appContext.appLocalStorage = new AppLocalStorage(userUiProfile.apiUserId);
        appContext.websiteProfile = websiteProfile;
        appContext.userUiProfile = userUiProfile;
        this.postNotification('warning', 'You are logged in but your session is blocked');
        break;

      case 'auth code':
        this.postNotification('info', 'An authorization code has been sent to your email');
        break;

      default:
        break;
    }

    this.setState(
    {
      sessionStatus: userSessionData.sessionStatus,
      appContext: appContext
    });

  }

  clearNotifier()
  {
    this.setState({ notificationText: '' });
  }

  /**
    * Unconditionally close the modal (as opposed to toggle)
    *
    */
  closeAppModal()
  {
    this.setState(
    {
      modalIsOpen: false,
    });
  }

  async componentDidMount()
  {
    if (this.componentMounted) return;                // React Strict mode is a PITA. They call componentDidMount twice...
    this.componentMounted = true;                               
    
    var userUiProfile;
    var websiteProfile;
    var sessionStatus = this.state.sessionStatus;
    var sessionInfo;
    var appLocalStorage;
    let appContext = this.state.appContext;

    if (null !== appContext.userSession.getSessionId())
    {
      userUiProfile = await this.fetchUserUiProfile();      
      // An error in this API call leaves userUiProfile undefined.
      if (undefined !== userUiProfile) appLocalStorage = new AppLocalStorage(userUiProfile.apiUserId);      
      sessionInfo = await appContext.userSession.getSessionInfo(this.dbTwig);
      if (undefined !== sessionInfo) sessionStatus = sessionInfo.sessionStatus;
    }      
    else
      sessionStatus = 'no session';

    websiteProfile = await this.fetchWebsiteProfile(false);

    appContext.userUiProfile = userUiProfile;
    appContext.appLocalStorage = appLocalStorage;
    appContext.websiteProfile = websiteProfile;
    this.setState({appContext: appContext, sessionStatus: sessionStatus});
  }

  constructor(props)
  {
    super();

    this.postNotification = this.postNotification.bind(this);
    this.authorize = this.authorize.bind(this);
    this.signout = this.signout.bind(this);
    this.clearNotifier = this.clearNotifier.bind(this);
    this.openAppModal = this.openAppModal.bind(this);
    this.openErrorModal = this.openErrorModal.bind(this);
    this.closeAppModal = this.closeAppModal.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.toggleErrorModal = this.toggleErrorModal.bind(this);
    this.setSessionStatus = this.setSessionStatus.bind(this);
    this.fetchWebsiteProfile = this.fetchWebsiteProfile.bind(this);
    this.fetchUserUiProfile = this.fetchUserUiProfile.bind(this);
    this.resizeCanvas = this.resizeCanvas.bind(this);

    this.state =
    {
      notificationText: '',
      notificationType: 'info',
      sessionStatus: 'unknown',
      errorModalIsOpen: false,
      modalIsOpen: false,
      modalTitle: '',
      modalMessage: '',
      minCardHeight: window.innerHeight * .9,
      appContext:   
      {
        openAppModal: this.openAppModal,
        openErrorModal: this.openErrorModal,
        postNotification: this.postNotification,
        userSession: new UserSession(),
        websiteProfile: undefined,
        fetchWebsiteProfile: this.fetchWebsiteProfile,
        authorize: this.authorize,
        closeAppModal: this.closeAppModal,
        signout: this.signout,
        dbTwig: this.dbTwig,
        extensions: this.extensions
      }
    }

    this.dbTwig = new DbTwig(this.state.appContext);
    this.state.appContext.dbTwig = this.dbTwig;

    window.addEventListener('resize', this.resizeCanvas);
  }

  extensions =
  {
    OCIObjectStorage: 'OCIObjectStorage',
    Automation: 'Automation',
    AI: 'AI Search and Vector Data'
  }

  async fetchUserUiProfile()
  {
    let result = await this.dbTwig.callRestAPI('dgBunker', 'getUserProfile');
    if ('success' !== result.status) return this.dbTwig.apiErrorHandler(result, 'Error retrieving user UI profile');
    return result.jsonData;
  }

  async fetchWebsiteProfile(updateContext)
  {
    let result = await this.dbTwig.callRestAPI('dgBunker', 'getObjectVaultProfile');
    if ('success' !== result.status) return this.dbTwig.apiErrorHandler(result, 'Error loading website profile');
    if (updateContext)
    {
      let appContext = this.state.appContext;
      appContext.websiteProfile = result.jsonData;
      this.setState({appContext: appContext});
    }
    return result.jsonData;
  }
  
  /**
    * Handles modal open requests from other components
    *
    * modalTitle    string   Text for popup title
    * modalMessage  string   Text for popup body
    *
    */
   openAppModal(modalTitle, modalMessage)
   {
     this.setState(
     {
       modalTitle: modalTitle,
       modalMessage: modalMessage,
       modalIsOpen: true,
     });
   }
 
   openErrorModal(modalTitle, modalMessage)
   {
     this.setState(
     {
       modalTitle: modalTitle,
       modalMessage: modalMessage,
       errorModalIsOpen: true,
     });
   }
 
  postNotification(notificationType, notificationText)
  {
    this.setState({ notificationText: notificationText, notificationType: notificationType });
  }

  render()
  {
    let modalSize = (this.state.modalMessage.length > 125 ? 'lg' : null);

    if ('unknown' === this.state.sessionStatus) 
      return(<div><PopupNotification notificationText={this.state.notificationText} notificationType={this.state.notificationType} clearNotifier={this.clearNotifier}/></div>);

    return(
      <AppContext.Provider value={this.state.appContext}>
        <PopupNotification notificationText={this.state.notificationText} notificationType={this.state.notificationType} clearNotifier={this.clearNotifier}/>
        <Router>
          <Modal size={modalSize} isOpen={this.state.modalIsOpen} toggle={this.toggleModal} autoFocus={false}> 
            <ModalHeader toggle={this.toggleModal}>{this.state.modalTitle}</ModalHeader>
            <ModalBody >{this.state.modalMessage}</ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={this.toggleModal} autoFocus>OK</Button>
            </ModalFooter>
          </Modal>
          <Modal size={modalSize} isOpen={this.state.errorModalIsOpen} toggle={this.toggleErrorModal} autoFocus={false}> 
            <ModalHeader toggle={this.toggleErrorModal}>{this.state.modalTitle}</ModalHeader>
            <ModalBody >
              <CopyToClipboard text={this.state.modalMessage} onCopy={() => this.postNotification('info', 'Error message copied to paste buffer')} >
                <span title='Click to copy error message' style={{whiteSpace: 'pre-line'}}>{this.state.modalMessage}</span>
              </CopyToClipboard>                  
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={this.toggleErrorModal} autoFocus>OK</Button>
            </ModalFooter>
          </Modal>
          <MainMenu sessionStatus={this.state.sessionStatus} />
          <Card style={{minHeight: this.state.minCardHeight}}>
            <CardBody style={{minHeight: "90%"}}>
              <Container fluid>
                <Row>
                  <Col xs="12">
                  { 'unknown' !== this.state.sessionStatus && undefined !== this.state.appContext.websiteProfile &&
                    <AppRoutes sessionStatus={this.state.sessionStatus} closeAppModal={this.closeAppModal} setSessionStatus={this.setSessionStatus}/>
                  }
                  </Col>
                </Row>
              </Container>
            </CardBody>
            <CardFooter>AsterionDB WebApp {window.webAppVersion} Copyright &copy; {new Date().getFullYear()} by Asterion Database Technology Group, LLC.  All Rights Reserved.</CardFooter>
          </Card>
        </Router>
      </AppContext.Provider>
    );
  }

  resizeCanvas()
  {
    this.setState({minCardHeight: window.innerHeight * .9});
  }

  setSessionStatus(sessionStatus)
  {
    if ('active' === sessionStatus) this.state.appContext.userSession.setSessionStatus(sessionStatus);
    this.setState({sessionStatus: sessionStatus});
  }

  /**
    * Handles toggle requests from the modal
    *
    */
  async signout(event)
  {
    if (typeof event !== 'undefined') event.preventDefault();
    await this.dbTwig.callRestAPI('icam', 'terminateUserSession');

    this.state.appContext.userSession.removeUserSessionData();
 
    this.setState({sessionStatus: 'no session'});
  }
 
  toggleErrorModal()
  {
    this.setState({errorModalIsOpen: !this.state.errorModalIsOpen});
  }

  toggleModal()
  {
    this.setState({modalIsOpen: !this.state.modalIsOpen});
  }
}

export default App
